import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import { getOrgProfile } from "../../actions/org/OrgProfileActions";

// Utils and Constants
import { toLocaleString } from "../../common-utils/string-utils/StringUtils";
import { constructSingleDateString } from "../../common-utils/date-utils/DateUtils";
import { calculatePercentage } from "../../common-utils/number-utils/NumberUtils";
import { DATE_FORMATS } from "../../constants/GeneralConstants";

// Components
import PageHeader from "../../mavin/components/page-header/PageHeader";
import Spinner from "../../components/spinner/Spinner";
import { ProgressBar } from "../../components/progress-bar/ProgressBar";

// Page Components
function OrgInfoCard({ orgProfile }) {
  const { name, addressStr, contactNo, website } = orgProfile || {};

  return (
    <div className="card p-3 mb-4 rounded-lg bg-alt">
      <h3>{name}</h3>
      <div className="mt-2">
        <p className="mb-2">{addressStr || "--"}</p>
        <p className="mb-0">Contact: {contactNo || "--"}</p>
        <p>Website: {website || "--"}</p>
      </div>
    </div>
  );
}

function SubscriptionInfoCard({ orgProfile }) {
  const {
    subscriptionDurationInDays,
    subscriptionExpiryTimestamp,
    subscriptionStartTimestamp,
  } = orgProfile || {};

  const durationString = toLocaleString(subscriptionDurationInDays);
  const startDate = constructSingleDateString(
    subscriptionStartTimestamp,
    DATE_FORMATS.full_month_with_date_year
  );
  const expiryDate = constructSingleDateString(
    subscriptionExpiryTimestamp,
    DATE_FORMATS.full_month_with_date_year
  );

  const subscriptionData = [
    { label: "Duration (Days)", value: durationString },
    { label: "Start Date", value: startDate },
    { label: "Expiry Date", value: expiryDate },
  ];

  return (
    <div className="card p-3 mb-4 rounded-lg bg-alt">
      <h3>Subscription Info</h3>
      <div className="mt-2">
        {subscriptionData.map((item, i) => {
          const { label, value } = item;
          return (
            <p key={i} className="mb-0">
              {label}: <span className="font-weight-bold">{value}</span>
            </p>
          );
        })}
      </div>
    </div>
  );
}

function ApiCallsInfoCard({ orgProfile }) {
  const { totalApiCallsMade, apiLimit, accessAPIs = [] } = orgProfile || {};

  const apiCallsData = [
    {
      label: "Access APIs",
      value: accessAPIs.length > 0 ? accessAPIs.join(", ") : "--",
    },
    {
      label: "Usage Limit",
      value: toLocaleString(apiLimit),
    },
    {
      label: "Calls Made",
      value: toLocaleString(totalApiCallsMade) || 0,
    },
    {
      label: "Remaining Calls",
      value: toLocaleString(apiLimit - totalApiCallsMade) || 0,
    },
  ];

  // usage percentage
  const apiUsagePercentage = calculatePercentage(
    Number(totalApiCallsMade),
    Number(apiLimit)
  );

  return (
    <div className="card p-3 mb-4 rounded-lg bg-alt">
      <div className="d-flex align-items-center justify-content-between">
        <h3>API Usage</h3>
        <div className="d-flex align-items-center">
          <p className="mb-2 d-flex align-items-center mr-4">
            <span className="border border-dark mr-1 square bg-light-green"></span>
            Calls Made
          </p>
          <p className="mb-2 d-flex align-items-center">
            <span className="border border-dark mr-1 square bg-light-blue"></span>
            Usage Limit
          </p>
        </div>
      </div>
      <div className="mt-2">
        {apiCallsData.map((item, i) => {
          const { label, value } = item;
          return (
            <p key={i} className="mb-0">
              {label}: <span className="font-weight-bold">{value}</span>
            </p>
          );
        })}

        {/* Progress Bar */}
        <div className="mt-2">
          <ProgressBar
            className="bg-light-green"
            percentage={apiUsagePercentage}
          />
          <ProgressBar className="bg-light-blue" percentage={100} />
        </div>
      </div>
    </div>
  );
}

/**
 * Org Api Subscribed HomePage
 */
export default function OrgApiSubscribedHomePage() {
  const dispatch = useDispatch();

  // Selector
  const orgProfile = useSelector((state) => state.orgProfile.orgProfileInfo);
  const orgProfileLoading = useSelector((state) => state.orgProfile.loading);

  // check if data present

  // call api only if data not present
  useEffect(() => {
    if (!orgProfile || Object.keys(orgProfile).length === 0) {
      dispatch(getOrgProfile());
    }
  }, [dispatch, JSON.stringify(orgProfile)]);

  // page loading
  if (orgProfileLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <div className="content-wrapper">
      <PageHeader title={"Dashboard"} border={true} shadow={true} />

      <div className="page-content container-fluid">
        {/* org info Card */}
        <OrgInfoCard orgProfile={orgProfile} />

        {/* Subscription Info Card */}
        <SubscriptionInfoCard orgProfile={orgProfile} />

        {/* API Calls Info Card */}
        <ApiCallsInfoCard orgProfile={orgProfile} />
      </div>
    </div>
  );
}
